// PaintingRegister.jsx: Painting register (2026-09-29).
// Coating systems, product data sheets, DFT results per system, ambient
// sessions and surface profiles, all from /api/paint (tenant scoped). Records only.

import React, { useEffect, useMemo, useState } from "react";
import { Card, Box, Typography } from "@mui/material";
import ApiCall from "../../utils/apicall";
import { StatusPill } from "../../components/WeldAssure";
import RegisterShell, { fmtDate } from "./RegisterShell";

const listOf = (res, key) => {
  const d = res?.data?.data;
  if (Array.isArray(d)) return d;
  return (d && d[key]) || [];
};

export default function PaintingRegister() {
  const [systems, setSystems] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [dft, setDft] = useState({}); // coating system id -> { reading_count, pass_count, fail_count }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, ds, am, sp] = await Promise.all([
          ApiCall.get("/paint/coating-systems"),
          ApiCall.get("/paint/data-sheets"),
          ApiCall.get("/paint/ambient-sessions"),
          ApiCall.get("/paint/surface-profiles"),
        ]);
        const sys = listOf(s, "systems");
        setSystems(sys); setSheets(listOf(ds, "sheets")); setSessions(listOf(am, "sessions")); setProfiles(listOf(sp, "profiles"));
        const jobs = [...new Set(sys.map((x) => x.job_id).filter(Boolean))];
        const byId = {};
        await Promise.all(jobs.map(async (jid) => {
          try {
            const c = await ApiCall.get(`/paint/compliance/${jid}`);
            (c.data?.data?.coatingSystems || []).forEach((x) => { byId[x.id] = x; });
          } catch (_) { /* job summary unavailable; counts stay blank */ }
        }));
        setDft(byId);
      } catch (err) { setError(err?.response?.data?.message || "Could not load painting records."); }
      finally { setLoading(false); }
    })();
  }, []);

  const totals = useMemo(() => Object.values(dft).reduce((a, x) => ({
    readings: a.readings + Number(x.reading_count || 0), fail: a.fail + Number(x.fail_count || 0),
  }), { readings: 0, fail: 0 }), [dft]);

  const kpis = [
    { label: "COATING SYSTEMS", value: systems.length },
    { label: "DATA SHEETS", value: sheets.length },
    { label: "DFT READINGS", value: totals.readings },
    { label: "DFT OUT OF SPEC", value: totals.fail },
  ];

  const sheetName = (id) => sheets.find((x) => x.id === id)?.product_name;

  return (
    <RegisterShell
      title="Painting"
      subtitle="Coating systems, product data sheets, DFT results, ambient conditions and surface profiles on record."
      guide={{ why: "See what coating system each job specifies and what the inspection records show against it.", who: "Paint foremen and coating inspectors.", what: "Systems, data sheets, DFT counts, ambient sessions and surface profiles.", how: "Read only. Records are entered from the inspection screens.", when: "Before and after each coat.", standard: "Project coating specification; SSPC-PA 2 DFT measurement" }}
      kpis={kpis}
      columns={["COATING SYSTEM", "JOB", "SURFACE PREP", "PRIMER", "TOTAL DFT (MILS)", "SPEC", "DFT READINGS", "RESULT"]}
      rows={systems}
      loading={loading}
      error={error}
      emptyText="No coating systems on file."
      renderRow={(s) => {
        const d = dft[s.id];
        const n = Number(d?.reading_count || 0); const f = Number(d?.fail_count || 0);
        return (
          <tr key={s.id}>
            <td>{s.system_name}</td>
            <td>{s.job_id ? `#${s.job_id}` : ""}</td>
            <td>{s.surface_prep || ""}</td>
            <td>{s.primer_product || sheetName(s.primer_pds_id) || ""}</td>
            <td>{s.total_dft_min ?? ""} to {s.total_dft_max ?? ""}</td>
            <td>{s.spec_reference || ""}</td>
            <td>{d ? n : ""}</td>
            <td>{!d ? "" : n === 0 ? <StatusPill label="No readings" kind="neutral" /> : f > 0 ? <StatusPill label={`${f} out of spec`} kind="danger" /> : <StatusPill label="All within spec" kind="success" />}</td>
          </tr>
        );
      }}
      footnote={`Ambient sessions on record: ${sessions.length}. Surface profiles on record: ${profiles.length}.`}
    >
      {sheets.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: 0.4 }}>PRODUCT DATA SHEETS</Typography>
          {sheets.map((p) => (
            <Box key={p.id} sx={{ display: "flex", gap: 2, py: 0.75, borderTop: "1px solid #EEF0F3", fontSize: 14, flexWrap: "wrap" }}>
              <Box sx={{ minWidth: 220, fontWeight: 600 }}>{p.product_name}</Box>
              <Box sx={{ minWidth: 140 }}>{p.manufacturer || ""}</Box>
              <Box sx={{ minWidth: 100 }}>{p.coating_layer || ""}</Box>
              <Box sx={{ minWidth: 160 }}>DFT {p.specified_dft_min ?? ""} to {p.specified_dft_max ?? ""} mils</Box>
              <Box sx={{ minWidth: 160 }}>{p.batch_coc_number ? `Batch CoC ${p.batch_coc_number}` : "No batch CoC"}</Box>
              <Box>{fmtDate(p.created_at)}</Box>
            </Box>
          ))}
        </Card>
      )}
    </RegisterShell>
  );
}
